import { useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import {
  AlertOctagon,
  AlertTriangle,
  ArrowUpRight,
  Package,
  PackagePlus,
  X,
} from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMxn } from "@/components/reports/KpiCard";

const SEVERITY = {
  critical: {
    label: "Crítico",
    tone: "bg-red-100 text-red-800 border-red-200",
    Icon: AlertOctagon,
  },
  warning: {
    label: "Bajo mínimo",
    tone: "bg-amber-100 text-amber-800 border-amber-200",
    Icon: AlertTriangle,
  },
};

function severityOf(it) {
  if (!it.current_stock || Number(it.current_stock) <= 0) return "critical";
  return "warning";
}

export function ReorderAlert({ branchId }) {
  const { isSuperAdmin, activeTenantId, branches, activeBranch } = useTenant();
  const { hasPermission } = useAuth();
  const canRegister = hasPermission("inventory:create");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const params = useMemo(() => {
    const p = {};
    if (branchId) p.branch_id = branchId;
    if (isSuperAdmin && activeTenantId) p.tenant_id = activeTenantId;
    return p;
  }, [branchId, isSuperAdmin, activeTenantId]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/low-stock", { params });
      setItems(data?.items || []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const counts = useMemo(() => {
    const c = { critical: 0, warning: 0, total_gap_value: 0 };
    items.forEach((it) => {
      c[severityOf(it)] += 1;
      c.total_gap_value += Number(it.suggested_purchase_value || 0);
    });
    return c;
  }, [items]);

  const preview = useMemo(() => {
    // sort: critical first, then largest gap
    return [...items]
      .sort((a, b) => {
        const sa = severityOf(a) === "critical" ? 0 : 1;
        const sb = severityOf(b) === "critical" ? 0 : 1;
        if (sa !== sb) return sa - sb;
        return Number(b.gap || 0) - Number(a.gap || 0);
      })
      .slice(0, 3);
  }, [items]);

  if (!loading && items.length === 0) return null;

  return (
    <>
      <div
        className="border rounded-lg border-amber-200 bg-amber-50/40 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        data-testid="reorder-alert"
      >
        <div className="flex items-start gap-3 flex-1">
          <div className="h-10 w-10 rounded-md bg-amber-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-amber-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-amber-950">
                Refacciones a re-ordenar
              </h3>
              {counts.critical > 0 && (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${SEVERITY.critical.tone}`}
                  data-testid="reorder-critical-badge"
                >
                  {counts.critical} crítico{counts.critical === 1 ? "" : "s"}
                </span>
              )}
              {counts.warning > 0 && (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${SEVERITY.warning.tone}`}
                  data-testid="reorder-warning-badge"
                >
                  {counts.warning} bajo mínimo
                </span>
              )}
            </div>
            <div className="text-xs text-amber-900/80 mt-1">
              {loading ? (
                <Skeleton className="h-3 w-40" />
              ) : (
                <>
                  {items.length} refacc{items.length === 1 ? "ión" : "iones"} bajo o en cero.
                  {counts.total_gap_value > 0 && (
                    <> Compra sugerida ≈ <b>{formatMxn(counts.total_gap_value)}</b>.</>
                  )}
                </>
              )}
            </div>

            {!loading && preview.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="reorder-preview">
                {preview.map((it) => {
                  const sev = severityOf(it);
                  const Icon = SEVERITY[sev].Icon;
                  return (
                    <span
                      key={it.part_id}
                      className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${SEVERITY[sev].tone}`}
                      title={it.name}
                    >
                      <Icon className="h-3 w-3" />
                      <span className="font-mono">{it.sku}</span>
                      <span className="opacity-70">·</span>
                      <span>
                        {it.current_stock}/{it.min_stock}
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="bg-white"
            onClick={() => setOpen(true)}
            data-testid="reorder-open-btn"
          >
            Ver y re-ordenar <ArrowUpRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      <ReorderDialog
        open={open}
        onOpenChange={setOpen}
        items={items}
        branches={branches}
        defaultBranchId={branchId || activeBranch?.id || branches?.[0]?.id || ""}
        canRegister={canRegister}
        isSuperAdmin={isSuperAdmin}
        activeTenantId={activeTenantId}
        onRefresh={fetchItems}
      />
    </>
  );
}

function ReorderDialog({
  open,
  onOpenChange,
  items,
  branches,
  defaultBranchId,
  canRegister,
  isSuperAdmin,
  activeTenantId,
  onRefresh,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ quantity: 0, unit_cost: "", branch_id: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const openRow = (it) => {
    setExpandedId(it.part_id);
    setForm({
      quantity: Number(it.gap || 0),
      unit_cost: it.unit_cost || "",
      branch_id: defaultBranchId,
      notes: `Re-orden desde alerta · min ${it.min_stock}`,
    });
  };

  const submit = async (it) => {
    if (!form.branch_id) {
      toast.error("Selecciona una sucursal");
      return;
    }
    const qty = Number(form.quantity);
    if (!qty || qty <= 0) {
      toast.error("La cantidad debe ser mayor a cero");
      return;
    }
    setSaving(true);
    try {
      await api.post(
        "/stock-movements",
        {
          part_id: it.part_id,
          branch_id: form.branch_id,
          quantity_delta: qty,
          movement_type: "purchase",
          unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
          notes: form.notes || null,
          reference_type: "reorder_alert",
        },
        { params: isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {} }
      );
      toast.success(`Entrada registrada · ${it.name}`);
      setExpandedId(null);
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const sa = severityOf(a) === "critical" ? 0 : 1;
      const sb = severityOf(b) === "critical" ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return Number(b.gap || 0) - Number(a.gap || 0);
    });
  }, [items]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[85vh] flex flex-col p-0"
        data-testid="reorder-dialog"
      >
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="tracking-tight flex items-center gap-2">
            <Package className="h-5 w-5 text-amber-700" />
            Refacciones a re-ordenar
          </DialogTitle>
          <DialogDescription>
            Refacciones con stock ≤ mínimo. Selecciona una para registrar la entrada directa (movimiento de compra).
            {!canRegister && (
              <span className="block mt-1 text-amber-700">
                Tu rol permite ver la alerta, pero no registrar movimientos. Contacta a Almacén o Gerencia.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Severidad</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead className="text-right">Mínimo</TableHead>
                <TableHead className="text-right">Sugerido</TableHead>
                <TableHead className="text-right">Costo compra</TableHead>
                <TableHead className="text-right">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((it) => {
                const sev = severityOf(it);
                const cfg = SEVERITY[sev];
                const isOpen = expandedId === it.part_id;
                return (
                  <Fragment key={it.part_id}>
                    <TableRow data-testid={`reorder-row-${it.part_id}`}>
                      <TableCell>
                        <Badge className={`${cfg.tone} border`}>{cfg.label}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                      <TableCell>
                        <div className="font-medium">{it.name}</div>
                        <div className="text-xs text-muted-foreground">{it.category || "—"}</div>
                      </TableCell>
                      <TableCell className="text-right">{it.current_stock}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {it.min_stock}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{it.gap}</TableCell>
                      <TableCell className="text-right">
                        {formatMxn(it.suggested_purchase_value || 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        {canRegister && (
                          <Button
                            size="sm"
                            variant={isOpen ? "outline" : "default"}
                            className={isOpen ? "" : "btn-tenant"}
                            onClick={() =>
                              isOpen ? setExpandedId(null) : openRow(it)
                            }
                            data-testid={`reorder-register-${it.part_id}`}
                          >
                            {isOpen ? (
                              <X className="h-4 w-4" />
                            ) : (
                              <>
                                <PackagePlus className="h-4 w-4 mr-1" />
                                Registrar entrada
                              </>
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow
                        className="bg-slate-50/60"
                        data-testid={`reorder-form-${it.part_id}`}
                      >
                        <TableCell colSpan={8} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div className="space-y-1.5">
                              <Label className="text-[11px] uppercase tracking-wider">
                                Cantidad
                              </Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.quantity}
                                onChange={(e) =>
                                  setForm({ ...form, quantity: e.target.value })
                                }
                                data-testid={`reorder-qty-${it.part_id}`}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-[11px] uppercase tracking-wider">
                                Sucursal destino
                              </Label>
                              <Select
                                value={form.branch_id}
                                onValueChange={(v) =>
                                  setForm({ ...form, branch_id: v })
                                }
                              >
                                <SelectTrigger data-testid={`reorder-branch-${it.part_id}`}>
                                  <SelectValue placeholder="Selecciona sucursal" />
                                </SelectTrigger>
                                <SelectContent>
                                  {branches.map((b) => (
                                    <SelectItem key={b.id} value={b.id}>
                                      {b.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-[11px] uppercase tracking-wider">
                                Costo unitario
                              </Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.unit_cost}
                                onChange={(e) =>
                                  setForm({ ...form, unit_cost: e.target.value })
                                }
                                data-testid={`reorder-cost-${it.part_id}`}
                              />
                            </div>
                            <div className="space-y-1.5 md:col-span-1">
                              <Label className="text-[11px] uppercase tracking-wider">
                                Notas
                              </Label>
                              <Textarea
                                rows={1}
                                value={form.notes}
                                onChange={(e) =>
                                  setForm({ ...form, notes: e.target.value })
                                }
                                data-testid={`reorder-notes-${it.part_id}`}
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setExpandedId(null)}
                            >
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              className="btn-tenant"
                              disabled={saving}
                              onClick={() => submit(it)}
                              data-testid={`reorder-submit-${it.part_id}`}
                            >
                              {saving ? "Registrando…" : "Confirmar entrada"}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>

          {items.length === 0 && (
            <div className="text-sm text-muted-foreground p-6 text-center">
              Sin refacciones bajo mínimo.
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border">
          <div className="text-xs text-muted-foreground flex-1">
            La entrada se registra como <b>movimiento de compra</b> (StockMovement).
            No genera OC formal — trazabilidad queda en el kardex.
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
